import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Container } from 'react-bootstrap'
import styled from 'styled-components'
import { _ } from 'utils'
import { useSpaceParams } from 'hooks'
import { NotFound } from 'components'
import { requestStatusSelector } from 'features/booth/config/fetchBoothSlice'
import { eventPreviewSelector } from 'features/auth/eventPreviewSlice'
import FullPageLoading from 'components/FullPageLoading'
import { deleteDocument, deleteVideo } from './actions'
import { selectBooth } from './fetchBoothSlice'
import { AssetColorCard, AssetImageCard } from './AssetCard'
import { DocumentItem, VideoItem } from './Items'
import GeneralInfo from './GeneralInfo'
import DocumentModal from './Items/DocumentModal'
import VideoModal from './Items/VideoModal'
import SortableList from './SortableList'
import BoothPreview, { BoothTypes } from './BoothPreview'
import useSaveBooth from './hooks/useSaveBooth'
import useFetchBooth from './hooks/useFetchBooth'
import useUpload from './hooks/useUpload'
import { VideoData } from './types'

const S = {
    Body: styled.div`
        padding-bottom: 24px;
        border-top: 1px solid rgba(176, 190, 197, 0.5);
        display: flex;
        justify-content: center;
        width: 100%;
    `,
    BoothPreviewWrapper: styled.div`
        width: 100%;
        background-color: white;
        display: flex;
        justify-content: center;
    `,
    Container: styled(Container)`
        margin-top: -90px;
        max-width: 980px;
    `,
}

const BoothConfig = () => {
    const dispatch = useDispatch()
    const booth = useSelector(selectBooth)
    const requestStatus = useSelector(requestStatusSelector)
    const saveBooth = useSaveBooth()
    const fetchBooth = useFetchBooth()
    const { eventSlug, spaceSlug } = useSpaceParams()
    const standUpload = useUpload('stand_image')
    const boothLogoUpload = useUpload('booth_logo')
    const eventPreview = useSelector(eventPreviewSelector)

    const updateColor = async (value: string) => {
        await saveBooth({ color: value })
        fetchBooth()
    }

    const onDeleteDocument = async (id: number) => {
        await dispatch(
            deleteDocument({
                id,
                boothSlug: spaceSlug,
                eventSlug,
            })
        )
        fetchBooth()
    }

    const onDeleteVideo = async (id: number) => {
        await dispatch(
            deleteVideo({
                id,
                boothSlug: spaceSlug,
                eventSlug,
            })
        )
        fetchBooth()
    }

    useEffect(() => {
        fetchBooth()
    }, [fetchBooth])

    if (requestStatus === 'loading') {
        return <FullPageLoading />
    }

    if (requestStatus === 'failed') {
        return <NotFound />
    }

    if (!booth) {
        return <div />
    }

    const {
        color, image, booth_logo, documents = [], videos = []
    } = booth

    const documentAddError = booth.documents.length >= booth.max_documents
        && `Max amount of uploads (${booth.max_documents}) has been reached`
    const videosAddError = booth.videos.length >= booth.max_videos
        && `Max amount of uploads (${booth.max_videos}) has been reached`

    return (
        <>
            <S.BoothPreviewWrapper>
                <BoothPreview
                    boothType={booth.type as BoothTypes}
                    boothBackground={booth.image}
                    boothLogo={booth.booth_logo}
                    color={booth.color}
                    video={videos?.length > 0 ? (videos[0] as VideoData) : null}
                />
            </S.BoothPreviewWrapper>

            <S.Body>
                <S.Container>
                    <Row className="mb-4">
                        <Col md={4}>
                            <AssetColorCard
                                buttonText={_('booth.config.color.edit')}
                                defaultValue={color}
                                onUpdate={updateColor}
                                title={_('booth.config.color.title')}
                            />
                        </Col>
                        <Col md={4}>
                            <AssetImageCard
                                buttonChangeText={_(
                                    'booth.config.artwork.change'
                                )}
                                buttonUploadText={_(
                                    'booth.config.artwork.upload'
                                )}
                                defaultValue={image}
                                onUpdate={standUpload}
                                title={_('booth.config.stand_image.title')}
                            />
                        </Col>
                        <Col md={4}>
                            <AssetImageCard
                                buttonChangeText={_(
                                    'booth.config.booth_logo.change'
                                )}
                                buttonUploadText={_(
                                    'booth.config.booth_logo.upload'
                                )}
                                defaultValue={booth_logo}
                                onUpdate={boothLogoUpload}
                                title={_(
                                    'booth.config.booth_logo.change.title'
                                )}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col md={12}>
                            <GeneralInfo booth={booth} fetchBooth={fetchBooth} />
                        </Col>
                    </Row>
                    {booth.max_documents === 0 ? (
                        ''
                    ) : (
                        <Row className="mb-4">
                            <Col md={12}>
                                <SortableList
                                    items={documents}
                                    onDelete={onDeleteDocument}
                                    ModalComponent={DocumentModal}
                                    ItemComponent={DocumentItem}
                                    disableAddWithMessage={documentAddError}
                                    title={_('booth.config.documents.title')}
                                    addButton={_(
                                        'booth.config.documents.add_button'
                                    )}
                                    titleAdd={_(
                                        'booth.config.documents.add_item_title'
                                    )}
                                    titleEdit={_(
                                        'booth.config.documents.edit_item_title'
                                    )}
                                    showTranslatedIndicator={eventPreview?.enable_page_translation}
                                />
                            </Col>
                        </Row>
                    )}
                    {booth.max_videos === 0 ? (
                        ''
                    ) : (
                        <Row className="mb-4">
                            <Col md={12}>
                                <SortableList
                                    items={videos}
                                    onDelete={onDeleteVideo}
                                    ModalComponent={VideoModal}
                                    ItemComponent={VideoItem}
                                    disableAddWithMessage={videosAddError}
                                    title={_('booth.config.videos.title')}
                                    addButton={_(
                                        'booth.config.videos.add_button'
                                    )}
                                    titleAdd={_(
                                        'booth.config.videos.add_item_title'
                                    )}
                                    titleEdit={_(
                                        'booth.config.videos.edit_item_title'
                                    )}
                                />
                            </Col>
                        </Row>
                    )}
                </S.Container>
            </S.Body>
        </>
    )
}

export default BoothConfig
